import {useEffect, useMemo} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {QueryStatus} from '@reduxjs/toolkit/dist/query'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {getAccommodationPageHotelId} from 'modules/accommodation/selectors'
import {setRoomsTopOfferLock} from 'modules/accommodation/slice'
import {isVioOfferOrBundleSelected} from 'modules/accommodation/utils'
import {getMeta} from 'modules/meta/selectors'
import {
  getCheapestBofhTopOfferParams,
  getFilteredRoomsData,
  getHasRoomsTopOfferLock,
  getHotelApiRoomsParameters,
  getRoomsSearchKey
} from 'modules/searchApi/selectors'
import {getAutoRefreshInterval} from 'utils/autoRefresh'

import {useActiveWindowTimeout} from '@vio/core/src/_web/utils/browser/hooks/useActiveWindowTimeout'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
// eslint-disable-next-line no-restricted-imports
import {
  getIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'
import {makeTransformSapiRoomsResponse} from '@vio/core/src/room/business/transformSapiRoomsResponse'
import {useGetRoomsPollingQuery} from '@vio/core/src/sapi/services/searchApi'
import {getIsUserEligibleForDealFreeze} from '@vio/shared/src/dealFreeze/modules/selector'
import {setFirstEligibleDealFreezeRoom} from '@vio/shared/src/dealFreeze/modules/slice'
import {isOfferEligibleForDealFreeze} from '@vio/shared/src/dealFreeze/utils/OfferEligibility'

export const useGetRooms = () => {
  const toggle = useToggle()
  const isOfferCheckOnPpEnabled = toggle(
    'feature-offer-check-on-pp',
    false,
    true
  )
  const isPreClickOfferCheckValidationEnabled =
    toggle('feature-offer-check-selected-offer-ap', false, true) &&
    !isOfferCheckOnPpEnabled

  const hotelId = useSelector(getAccommodationPageHotelId)
  const roomsEndpointParams = useSelector(state =>
    getHotelApiRoomsParameters(state, hotelId)
  )
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isUserEligibleForDealFreeze = useSelector(
    getIsUserEligibleForDealFreeze
  )
  const dispatch = useDispatch()
  const isAuthLoading = useSelector(selectIsLoading)
  // We need to replicate the offers/sb ordering here in the same way as we do when rendering top-offers
  const {isComplete} = useAccommodationTopOffers({hotelId})
  const isVioOfferSelected = isVioOfferOrBundleSelected()
  const searchKey = useSelector(state => getRoomsSearchKey(state, hotelId))
  // Once locked, filtering the top offers must not pause the rooms request
  const hasTopOfferLock = useSelector(state =>
    getHasRoomsTopOfferLock(state, hotelId)
  )
  const topOfferParams = useSelector(state =>
    getCheapestBofhTopOfferParams(state, hotelId)
  )
  const offersLoadedOrVioOfferSelected =
    isComplete || isVioOfferSelected || hasTopOfferLock
  const {rooms, checkIn, checkOut} = roomsEndpointParams
  const eligibilityStatus = !isAuthenticated || isUserEligibleForDealFreeze
  const shouldSkip =
    !hotelId ||
    isAuthLoading ||
    // When reloading AP and rooms are optimized we wait for optimized roomsConfig
    !rooms ||
    // skip when top-offers are not loaded yet for this hotel and it's not a vio-clicker scenario
    !offersLoadedOrVioOfferSelected
  const {includeTaxes, includeLocalTaxes} = useSelector(getMeta)
  const transformResponse = makeTransformSapiRoomsResponse({
    includeTaxes,
    includeLocalTaxes,
    /**
     * Hacky way to clear isClicked flag from returned rooms-offers
     * when there is no selected offer params in the url, but rooms-offers needs to be called with
     * clickedOffer data to prevent price mismatch between top-offers and rooms-offers shown together on AP
     **/
    clearIsClickedFlag: !isVioOfferSelected
  })

  useEffect(() => {
    if (shouldSkip || isVioOfferSelected || hasTopOfferLock) return
    dispatch(setRoomsTopOfferLock({hotelId, searchKey, params: topOfferParams}))
  }, [
    dispatch,
    hasTopOfferLock,
    hotelId,
    isVioOfferSelected,
    searchKey,
    shouldSkip,
    topOfferParams
  ])

  const {refetch, ...result} = useGetRoomsPollingQuery(
    {
      transformResponse,
      ...roomsEndpointParams,
      isPreClickOfferCheckValidationEnabled
    },
    {
      skip: shouldSkip || !checkIn || !checkOut
    }
  )

  useActiveWindowTimeout({
    callbackTimeoutMs: getAutoRefreshInterval(),
    callback: () => {
      dispatch(
        trackEvent({
          category: Category.System,
          entity: Entity.PriceRefresh,
          action: Action.Displayed,
          page: Page.Accommodation
        })
      )
      refetch()
    }
  })

  useEffect(() => {
    // A logic to show DealFreezeBanner on First Deal Freeze room
    if (result.status === QueryStatus.fulfilled) {
      let eligibleOfferIndex = null
      const {rooms} = result.data

      for (let roomIndex = 0; roomIndex < rooms.length; roomIndex++) {
        const {offers, id: roomId} = rooms[roomIndex]
        for (let offerIndex = 0; offerIndex < offers.length; offerIndex++) {
          const isOfferEligible =
            isOfferEligibleForDealFreeze(offers[offerIndex]?.tags) &&
            eligibilityStatus

          if (isOfferEligible) {
            eligibleOfferIndex = offerIndex
            dispatch(setFirstEligibleDealFreezeRoom(roomId))
            break
          }
        }

        if (eligibleOfferIndex !== null) {
          break
        }
      }
    }
  }, [checkIn, dispatch, eligibilityStatus, result.data, result.status])

  const filteredData = useSelector(getFilteredRoomsData)

  return useMemo(
    () => ({
      ...result,
      ...filteredData,
      data: filteredData
    }),
    [filteredData, result]
  )
}
